// Adapts a deterministic acquisition scenario (from /api/acquisition/all)
// into the `result` shape consumed across the pitch view (SimpleIndicators,
// ExecutiveSummary, Financials, IndicatorGrid, FinancialTables, Deck,
// PlanAppendix). This makes every result card derive from the SELECTED
// scenario — a single source of truth — instead of the legacy monthly plan.

function npv(rate, flows) {
  return flows.reduce((sum, v, i) => sum + v / Math.pow(1 + rate, i), 0);
}

function irr(flows) {
  const nonzero = flows.filter((x) => x !== 0);
  if (!nonzero.length || flows[0] >= 0) return null;
  const f = (r) => npv(r, flows);
  let lo = -0.9, hi = 10;
  if (f(lo) * f(hi) > 0) return null;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    if (f(mid) > 0) lo = mid; else hi = mid;
  }
  return Math.round(((lo + hi) / 2) * 100 * 100) / 100;
}

const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

function adaptYear(y) {
  const revenue = y.revenue_net || 0;
  const cogs = y.direct_cost_adjusted || 0;
  const support = y.variable_provision || 0;
  const opex = (y.fixed_costs || 0) + (y.management_cost || 0);
  const gross = revenue - cogs;
  const ebitda = y.ebitda || 0;
  const depreciation = y.depreciation || 0;
  const ebit = ebitda - depreciation;
  const tax = y.tax || 0;
  const net = y.net_profit || 0;
  const oc = y.operating_cash || 0;
  return {
    year: y.year, units: y.units, revenue: r2(revenue), revenue_net: r2(revenue),
    equipment_revenue: r2(revenue), accessories_revenue: 0, training_revenue: 0, maintenance_revenue: 0,
    cogs: r2(cogs), gross_profit: r2(gross), support: r2(support), opex: r2(opex), events_cost: r2(y.events_cost || 0),
    ebitda: r2(ebitda), depreciation: r2(depreciation), ebit: r2(ebit), interest: 0, tax: r2(tax),
    net_profit: r2(net), working_capital: 0, working_capital_change: 0, loan_repayment: 0, loan_balance: 0,
    operating_cash_flow: r2(oc), cash_flow: r2(oc), closing_cash: r2(y.cumulative_operating_cash || 0),
    project_fcf: r2(oc), operating_result: r2(ebitda),
    gross_margin: revenue ? gross / revenue * 100 : null,
    ebitda_margin: revenue ? ebitda / revenue * 100 : null,
    net_margin: revenue ? net / revenue * 100 : null,
    dscr: null,
  };
}

export function scenarioToResult(sc, breakEven, opts = {}) {
  if (!sc || !Array.isArray(sc.years) || !sc.years.length) return null;
  const discountRate = opts.discountRate ?? 10;
  const years = sc.years;
  const capital = sc.capital_total_eur ?? 0;
  const flows = [-capital, ...years.map((y) => y.operating_cash || 0)];
  const val = npv(discountRate / 100, flows);
  const tir = irr(flows);

  // Payback in YEARS, derived from the SAME monthly-flow, same-investment-base
  // payback the dossier chart/KPI use (acquisition + transaction, + WC when the
  // scenario includes it). Falls back to annual interpolation only if the
  // monthly figure is unavailable, so both views always agree.
  let paybackAnnual = null, cum = -capital;
  for (let i = 0; i < years.length; i++) {
    const oc = years[i].operating_cash || 0;
    if (oc > 0 && cum + oc >= 0) { paybackAnnual = i + (-cum) / oc; break; }
    cum += oc;
  }
  const paybackMonths = sc.payback_total_capital_months;
  const payback = Number.isFinite(paybackMonths) ? paybackMonths / 12 : paybackAnnual;

  const y1 = years[0];
  const annualYears = years.map(adaptYear);

  // Monthly projection consumes the backend's corrected series (single source
  // of truth): each month's revenue/cost already derive from the REAL mix sold
  // that month (integer per-model distribution summing to the annual total),
  // not the annual average. The closing balance is the running cumulative of
  // the monthly net flows starting from an initial cash of zero, so every month
  // reconciles with the previous balance plus its flow — and the 12 months
  // reconcile with the annual totals.
  const series = (sc.monthly_cash_series || []).filter((p) => p.absolute_month <= 12);
  const perModel = y1.per_model || [];
  // Per-model monthly quantities come straight from the backend breakdown
  // (fallback to an even integer distribution only if the series is missing).
  const distribute = (n) => {
    const b = Math.floor((Number(n) || 0) / 12);
    const r = (Number(n) || 0) - b * 12;
    return Array.from({ length: 12 }, (_, i) => b + (i < r ? 1 : 0));
  };
  const perModelMonthly = {};
  perModel.forEach((pm) => {
    perModelMonthly[pm.id] = series.length
      ? series.map((p) => (p.units_by_model || {})[pm.id] || 0)
      : distribute(pm.units || 0);
  });

  // Events (already inside the annual fixed costs) are surfaced per month using
  // the plan's event calendar when available (each event on its planned month),
  // otherwise spread evenly. They are NEVER deducted again from EBITDA or cash.
  const events = (opts.events || []).filter((e) => e && e.enabled);
  const eventsAnnual = events.reduce((s, e) => s + (Number(e.budget) || 0), 0);
  const monthlyEvents = Array.from({ length: 12 }, () => 0);
  if (events.length && events.every((e) => e.month >= 1 && e.month <= 12)) {
    events.forEach((e) => { monthlyEvents[(e.month || 1) - 1] += Number(e.budget) || 0; });
  } else {
    for (let i = 0; i < 12; i++) monthlyEvents[i] = eventsAnnual / 12;
  }

  const months = series.map((p, i) => ({
    month: p.month ?? i + 1,
    units: p.units ?? 0,
    units_by_model: perModel.reduce((o, pm) => { o[pm.id] = perModelMonthly[pm.id][i]; return o; }, {}),
    sales_complete: true, auto_distribution: true,
    revenue: r2(p.revenue_net), equipment_revenue: r2(p.revenue_net), extra_revenue: 0,
    cogs: r2(p.direct_cost_adjusted), gross_profit: r2((p.revenue_net || 0) - (p.direct_cost_adjusted || 0)),
    support: r2(p.variable_provision),
    opex: r2((p.fixed_costs || 0) + (p.management_cost || 0)),
    events_cost: r2(p.events_cost ?? monthlyEvents[i] ?? 0),
    ebitda: r2(p.ebitda), depreciation: r2(p.depreciation), interest: 0,
    tax: r2(p.tax), net_profit: r2(p.net_profit), loan_repayment: 0,
    cash_flow: r2(p.cash),
    closing_cash: r2(p.cumulative_cash),
  }));
  const totalUnitsY1 = y1.units || 0;

  const revenue = y1.revenue_net || 0;
  const ebitda = y1.ebitda || 0;
  const net = y1.net_profit || 0;

  // Break-even derived from the ACTIVE scenario's year-1 composition (weighted
  // by the units of each model, since revenue/cost already aggregate them):
  //   contribution_total = revenue - equipment acquisition (incl. buffer) - variable provision
  //   contribution_per_unit = contribution_total / units
  //   break-even units = fixed costs / contribution_per_unit
  //   break-even revenue = fixed costs / (contribution_total / revenue)
  // When contribution is null/negative there is no break-even (fixed costs are
  // never covered) — surface null so the UI shows an adequate indication.
  const y1Cogs = y1.direct_cost_adjusted || 0;
  const y1Support = y1.variable_provision || 0;
  const y1Fixed = (y1.fixed_costs || 0) + (y1.management_cost || 0);
  const contributionTotal = revenue - y1Cogs - y1Support;
  const hasPositiveContribution = contributionTotal > 0 && y1.units > 0;
  const breakEvenUnits = hasPositiveContribution ? y1Fixed / (contributionTotal / y1.units) : null;
  const breakEvenRevenue = contributionTotal > 0 ? y1Fixed / (contributionTotal / revenue) : null;
  const contributionPerUnit = y1.units ? contributionTotal / y1.units : null;

  const metrics = {
    revenue: r2(revenue), gross_margin_percent: revenue ? (revenue - (y1.direct_cost_adjusted || 0)) / revenue * 100 : null,
    ebitda: r2(ebitda), ebitda_margin: revenue ? ebitda / revenue * 100 : null,
    net_profit: r2(net), net_margin: revenue ? net / revenue * 100 : null,
    break_even_units: breakEvenUnits == null ? null : r2(breakEvenUnits),
    break_even_revenue: breakEvenRevenue == null ? null : r2(breakEvenRevenue),
    contribution_per_unit: contributionPerUnit == null ? null : r2(contributionPerUnit),
    average_price_net: y1.units ? r2(revenue / y1.units) : null,
    average_cost: y1.units ? r2((y1.direct_cost_adjusted || 0) / y1.units) : null,
    cac: null, revenue_per_customer: null,
    initial_capex: r2(sc.acquisition_price_eur || 0),
    initial_startup: r2(sc.transaction_costs_eur || 0),
    initial_working_capital: r2(sc.working_capital_eur || 0),
    initial_project_investment: r2(capital), initial_cash: 0, minimum_cash: 0, additional_funding_needed: 0,
    npv: r2(val), irr: tir, payback_years: payback == null ? null : r2(payback),
    project_roi_percent: capital ? flows.reduce((a, b) => a + b, 0) / capital * 100 : null,
    dscr: null, sam_capture_percent: null,
    capital_breakdown: [
      { label: 'Aquisição', value: r2(sc.acquisition_price_eur || 0) },
      { label: 'Custos de transação', value: r2(sc.transaction_costs_eur || 0) },
      { label: 'Fundo de maneio', value: r2(sc.working_capital_eur || 0) },
    ],
  };

  return {
    scenario_key: sc.scenario_key, scenario_label: sc.label,
    sales_status: { defined_cells: 72, total_cells: 72, complete: true, state: 'complete' },
    years: annualYears, months, metrics,
    sales_mix: {
      mode: 'annual', total_units: y1.units || 0,
      rows: (y1.per_model || []).map((pm) => {
        const eff = totalUnitsY1 ? (pm.units || 0) / totalUnitsY1 * 100 : 0;
        const unitCostBuffered = pm.unit_cost_buffered ?? pm.unit_cost_base ?? 0;
        const gp = (pm.revenue_net || 0) - (pm.units || 0) * unitCostBuffered;
        return {
          id: pm.id, name: pm.name, units: pm.units, revenue: pm.revenue_net,
          discount_percent: pm.discount_percent_applied || 0,
          discount_applied: (pm.discount_percent_applied || 0) > 0,
          target_percent: r2(eff), effective_percent: r2(eff),
          cost_base: r2(pm.unit_cost_base ?? 0), cost_buffered: r2(unitCostBuffered),
          gross_profit: r2(gp), monthly_units: perModelMonthly[pm.id] || [],
        };
      }),
    },
    events: {
      count: events.length,
      budget: r2(eventsAnnual),
      leads: events.reduce((s, e) => s + (Number(e.leads) || 0), 0),
      monthly: series.length ? series.map((p) => r2(p.events_cost || 0)) : monthlyEvents.map((v) => r2(v)),
      distribution: 'by_event_month',
    },
    extra_lines: [],
    project_cash_flows: flows.map((v) => r2(v)),
    allocation: [{ name: 'Capital de aquisição', percent: 100, amount: r2(capital) }],
    average_price_net: metrics.average_price_net, average_cost: metrics.average_cost,
    gross_margin_percent: metrics.gross_margin_percent, contribution_per_unit: metrics.contribution_per_unit,
    break_even_units: metrics.break_even_units,
  };
}
